import {
  ToothCondition,
  IDentalChart,
  IPerioChart,
} from '@dataTypes/chartingFeature.types'
import { createEmptyPerioChartData } from '@utils/perio.utils'

/**
 * Single source of truth for charting condition catalog, panel labels,
 * type/material/surface registries, and tooth restriction rules.
 */

// Condition metadata interface
export interface ConditionMetadata {
  name: string
  description: string
  allowsType: boolean
  allowsMaterial: boolean
  allowsSurface?: boolean
  validTypes?: string[]
  validMaterials?: string[]
  canBeRecurrent: boolean
}

// Type metadata interface
export interface TypeMetadata {
  name: string
  description: string
  category: 'severity' | 'grade' | 'class'
}

// Material metadata interface
export interface MaterialMetadata {
  name: string
  description: string
}

// Surface metadata interface
export interface SurfaceMetadata {
  name: string
  description: string
  abbreviation: string
}

// CONDITIONS - Base conditions with metadata
export const CONDITIONS: Record<string, ConditionMetadata> = {
  // General states
  present: {
    name: 'Present',
    description: 'Tooth is present',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  missing: {
    name: 'Missing',
    description: 'Tooth is missing',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },

  // Caries
  caries: {
    name: 'Caries',
    description: 'Tooth decay or caries',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['D1', 'D2', 'D3', 'E1', 'E2'],
    canBeRecurrent: true,
  },

  recurring_caries: {
    name: 'Recurring caries',
    description:
      'Recurrent decay at a restoration margin (uses recurring caries overlays)',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['D1', 'D2', 'D3', 'E1', 'E2'],
    canBeRecurrent: false,
  },

  // Restorations
  filling: {
    name: 'Filling',
    description: 'Tooth filling/restoration',
    allowsType: false,
    allowsMaterial: true,
    validMaterials: ['composite', 'amalgam', 'ceramic', 'gold', 'temporary'],
    canBeRecurrent: true,
  },
  crown: {
    name: 'Crown',
    description: 'Dental crown',
    allowsType: false,
    allowsMaterial: true,
    validMaterials: [
      'composite',
      'zirconia',
      'pfm',
      'pfz',
      'metal',
      'ceramic',
      'gold',
    ],
    canBeRecurrent: false,
  },
  veneer: {
    name: 'Veneer',
    description: 'Dental veneer',
    allowsType: false,
    allowsMaterial: true,
    validMaterials: ['ceramic', 'composite', 'zirconia'],
    canBeRecurrent: false,
  },
  inlay: {
    name: 'Inlay',
    description: 'Inlay restoration',
    allowsType: false,
    allowsMaterial: true,
    validMaterials: ['gold', 'zirconia', 'ceramic', 'composite', 'metal'],
    canBeRecurrent: false,
  },
  onlay: {
    name: 'Onlay',
    description: 'Onlay restoration',
    allowsType: false,
    allowsMaterial: true,
    validMaterials: ['gold', 'zirconia', 'ceramic', 'composite', 'metal'],
    canBeRecurrent: false,
  },

  // Wear
  attrition: {
    name: 'Attrition',
    description: 'Tooth wear from grinding',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['slight', 'moderate', 'severe'],
    canBeRecurrent: false,
  },
  abrasion: {
    name: 'Abrasion/abfraction',
    description: 'Tooth wear from external factors',
    allowsType: true,
    allowsMaterial: false,
    allowsSurface: true,
    validTypes: ['slight', 'moderate', 'severe'],
    canBeRecurrent: false,
  },
  erosion: {
    name: 'Erosion',
    description: 'Tooth erosion from acid',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },

  // Cracks and fractures
  crack: {
    name: 'Crack',
    description: 'Tooth crack',
    allowsType: true,
    allowsMaterial: false,
    validTypes: [
      'vertical_root',
      'horizontal_root',
      'vertical_crown_buccal',
      'vertical_crown_lingual',
      'vertical_crown_palatal',
      'vertical_crown_occlusal',
      'horizontal_crown_buccal',
      'horizontal_crown_lingual',
      'horizontal_crown_palatal',
      'horizontal_crown_occlusal',
    ],
    canBeRecurrent: false,
  },
  fracture: {
    name: 'Fracture',
    description: 'Tooth fracture',
    allowsType: true,
    allowsMaterial: false,
    validTypes: [
      'mesiobuccal',
      'distobuccal',
      'mesiolingual',
      'distolingual',
      'mesiopalatal',
      'distopalatal',
      'incisal',
      'mifp',
      'difp',
      'mifl',
      'difl',
    ],
    canBeRecurrent: false,
  },

  // Preventive
  sealants: {
    name: 'Sealants',
    description: 'Protective sealant applied',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },

  // Prosthetics
  implant: {
    name: 'Implant',
    description: 'Dental implant',
    allowsType: false,
    allowsMaterial: true,
    validMaterials: ['titanium', 'zirconia', 'pfm'],
    canBeRecurrent: false,
  },
  bridge: {
    name: 'Bridge',
    description: 'Bridge replacement tooth',
    allowsType: true,
    allowsMaterial: true,
    validTypes: ['left_abutment', 'right_abutment', 'right_left_abutment'],
    validMaterials: ['composite', 'zirconia', 'pfm', 'pfz', 'metal', 'ceramic'],
    canBeRecurrent: false,
  },
  abutment: {
    name: 'Abutment',
    description: 'Bridge abutment tooth',
    allowsType: true,
    allowsMaterial: true,
    validTypes: ['left_abutment', 'right_abutment', 'right_left_abutment'],
    validMaterials: ['composite', 'zirconia', 'pfm', 'pfz', 'metal', 'ceramic'],
    canBeRecurrent: false,
  },

  // Impaction
  impacted: {
    name: 'Impacted',
    description: 'Impacted tooth',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['left', 'right', 'down', 'up'],
    canBeRecurrent: false,
  },

  // Periodontal
  furcation: {
    name: 'Furcation',
    description: 'Furcation involvement',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['C1', 'C2', 'C3'],
    canBeRecurrent: false,
  },
  bleeding: {
    name: 'Bleeding',
    description: 'Bleeding on probing',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  recession: {
    name: 'Gingival Recession',
    description: 'Gingival recession',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['class1', 'class2', 'class3', 'class4'],
    canBeRecurrent: false,
  },

  // Endodontic
  root_canal: {
    name: 'Root canal',
    description: 'Root canal treatment',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  pulpotomy: {
    name: 'Pulpotomy',
    description: 'Pulpotomy procedure',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  pulpectomy: {
    name: 'Pulpectomy',
    description: 'Pulpectomy procedure',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  apicoectomy: {
    name: 'Apicoectomy',
    description: 'Apicoectomy procedure',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  periapical: {
    name: 'Periapical Lesion',
    description: 'Periapical lesion',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  periradicular: {
    name: 'Periradicular Lesion',
    description: 'Periradicular lesion',
    allowsType: true,
    allowsMaterial: false,
    validTypes: ['slight', 'moderate', 'severe'],
    canBeRecurrent: false,
  },

  // Other
  drifted: {
    name: 'Drifted',
    description: 'Drifted tooth',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  retainer: {
    name: 'Retained primary',
    description: 'Retained primary tooth',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  supernumerary: {
    name: 'Supernumerary',
    description: 'Extra tooth',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
  sinus_tract: {
    name: 'Sinus tract',
    description: 'Sinus tract present',
    allowsType: false,
    allowsMaterial: false,
    canBeRecurrent: false,
  },
}

// TYPES - Severity/Grade classifications
export const TYPES: Record<string, TypeMetadata> = {
  // Decay grades
  D1: { name: 'D1', description: 'Early enamel decay', category: 'grade' },
  D2: {
    name: 'D2',
    description: 'Moderate decay into dentin',
    category: 'grade',
  },
  D3: { name: 'D3', description: 'Deep decay near pulp', category: 'grade' },
  E1: { name: 'E1', description: 'Early enamel erosion', category: 'grade' },
  E2: { name: 'E2', description: 'Moderate enamel erosion', category: 'grade' },

  // Severity levels
  slight: {
    name: 'Slight',
    description: 'Slight severity',
    category: 'severity',
  },
  moderate: {
    name: 'Moderate',
    description: 'Moderate severity',
    category: 'severity',
  },
  severe: {
    name: 'Severe',
    description: 'Severe severity',
    category: 'severity',
  },

  // Furcation classes
  C1: { name: 'Class I', description: 'Class I furcation', category: 'class' },
  C2: {
    name: 'Class II',
    description: 'Class II furcation',
    category: 'class',
  },
  C3: {
    name: 'Class III',
    description: 'Class III furcation',
    category: 'class',
  },

  // Recession classes
  class1: {
    name: 'Miller Class I',
    description: 'Miller Class I recession',
    category: 'class',
  },
  class2: {
    name: 'Miller Class II',
    description: 'Miller Class II recession',
    category: 'class',
  },
  class3: {
    name: 'Miller Class III',
    description: 'Miller Class III recession',
    category: 'class',
  },
  class4: {
    name: 'Miller Class IV',
    description: 'Miller Class IV recession',
    category: 'class',
  },

  // Crack types
  vertical_root: {
    name: 'Vertical Root',
    description: 'Vertical root crack',
    category: 'severity',
  },
  horizontal_root: {
    name: 'Horizontal Root',
    description: 'Horizontal root crack',
    category: 'severity',
  },
  vertical_crown_buccal: {
    name: 'Vertical Crown Buccal',
    description: 'Vertical crack in buccal crown',
    category: 'severity',
  },
  vertical_crown_lingual: {
    name: 'Vertical Crown Lingual',
    description: 'Vertical crack in lingual crown',
    category: 'severity',
  },
  vertical_crown_palatal: {
    name: 'Vertical Crown Palatal',
    description: 'Vertical crack in palatal crown',
    category: 'severity',
  },
  vertical_crown_occlusal: {
    name: 'Vertical Crown Occlusal',
    description: 'Vertical crack in occlusal crown',
    category: 'severity',
  },
  horizontal_crown_buccal: {
    name: 'Horizontal Crown Buccal',
    description: 'Horizontal crack in buccal crown',
    category: 'severity',
  },
  horizontal_crown_lingual: {
    name: 'Horizontal Crown Lingual',
    description: 'Horizontal crack in lingual crown',
    category: 'severity',
  },
  horizontal_crown_palatal: {
    name: 'Horizontal Crown Palatal',
    description: 'Horizontal crack in palatal crown',
    category: 'severity',
  },
  horizontal_crown_occlusal: {
    name: 'Horizontal Crown Occlusal',
    description: 'Horizontal crack in occlusal crown',
    category: 'severity',
  },

  // Fracture types
  mesiobuccal: {
    name: 'Mesiobuccal',
    description: 'Mesiobuccal fracture',
    category: 'severity',
  },
  distobuccal: {
    name: 'Distobuccal',
    description: 'Distobuccal fracture',
    category: 'severity',
  },
  mesiolingual: {
    name: 'Mesiolingual',
    description: 'Mesiolingual fracture',
    category: 'severity',
  },
  distolingual: {
    name: 'Distolingual',
    description: 'Distolingual fracture',
    category: 'severity',
  },
  mesiopalatal: {
    name: 'Mesiopalatal',
    description: 'Mesiopalatal fracture',
    category: 'severity',
  },
  distopalatal: {
    name: 'Distopalatal',
    description: 'Distopalatal fracture',
    category: 'severity',
  },
  incisal: {
    name: 'Incisal',
    description: 'Incisal fracture',
    category: 'severity',
  },
  mifp: {
    name: 'MIFP',
    description: 'Mesio Incisal Facial Palatal fracture',
    category: 'severity',
  },
  difp: {
    name: 'DIFP',
    description: 'Disto Incisal Facial Palatal fracture',
    category: 'severity',
  },
  mifl: {
    name: 'MIFL',
    description: 'Mesio Incisal Facial Lingual fracture',
    category: 'severity',
  },
  difl: {
    name: 'DIFL',
    description: 'Disto Incisal Facial Lingual fracture',
    category: 'severity',
  },

  // Impaction directions
  left: {
    name: 'Left',
    description: 'Impacted towards left',
    category: 'severity',
  },
  right: {
    name: 'Right',
    description: 'Impacted towards right',
    category: 'severity',
  },
  down: {
    name: 'Down',
    description: 'Impacted downward',
    category: 'severity',
  },
  up: { name: 'Up', description: 'Impacted upward', category: 'severity' },

  // Bridge support
  left_bridge: {
    name: 'Mesial',
    description: 'Mesial position',
    category: 'severity',
  },
  left_abutment: {
    name: 'Mesial',
    description: 'Mesial position',
    category: 'severity',
  },
  right_bridge: {
    name: 'Distal',
    description: 'Distal position',
    category: 'severity',
  },
  right_abutment: {
    name: 'Distal',
    description: 'Distal position',
    category: 'severity',
  },
  right_left_bridge: {
    name: 'Mesial-Distal',
    description: 'Mesial-distal position',
    category: 'severity',
  },
  right_left_abutment: {
    name: 'Mesial-Distal',
    description: 'Mesial-distal position',
    category: 'severity',
  },
}

// MATERIALS - Restoration materials
export const MATERIALS: Record<string, MaterialMetadata> = {
  // Crown materials
  zirconia: { name: 'Zirconia', description: 'Zirconia material' },
  pfm: { name: 'PFM', description: 'Porcelain-fused-to-metal' },
  pfz: { name: 'PFZ', description: 'Porcelain-fused-to-zirconia' },
  metal: { name: 'Metal', description: 'Metal crown' },
  ceramic: { name: 'Ceramic', description: 'All-ceramic/porcelain' },
  composite: { name: 'Composite', description: 'Composite resin' },
  gold: { name: 'Gold', description: 'Gold alloy' },

  // Filling materials
  amalgam: { name: 'Amalgam', description: 'Amalgam filling' },
  temporary: { name: 'Temporary', description: 'Temporary filling material' },

  // Other materials
  titanium: { name: 'Titanium', description: 'Titanium implant' },
}

// SURFACES - Tooth surfaces
export const SURFACES: Record<string, SurfaceMetadata> = {
  // Primary surfaces
  mesial: {
    name: 'Mesial',
    description: 'Mesial surface (toward midline)',
    abbreviation: 'M',
  },
  distal: {
    name: 'Distal',
    description: 'Distal surface (away from midline)',
    abbreviation: 'D',
  },
  buccal: {
    name: 'Buccal',
    description: 'Buccal surface (cheek side)',
    abbreviation: 'B',
  },
  lingual: {
    name: 'Lingual',
    description: 'Lingual surface (tongue side)',
    abbreviation: 'L',
  },
  occlusal: {
    name: 'Occlusal',
    description: 'Occlusal surface (chewing surface)',
    abbreviation: 'O',
  },
  palatal: {
    name: 'Palatal',
    description: 'Palatal surface (roof of mouth side)',
    abbreviation: 'P',
  },
  incisal: { name: 'Incisal', description: 'Incisal edge', abbreviation: 'I' },

  // Combined surfaces
  mesiobuccal: {
    name: 'Mesiobuccal',
    description: 'Mesiobuccal cusp/surface',
    abbreviation: 'MOB',
  },
  distobuccal: {
    name: 'Distobuccal',
    description: 'Distobuccal cusp/surface',
    abbreviation: 'DOB',
  },
  mesiopalatal: {
    name: 'Mesiopalatal',
    description: 'Mesiopalatal cusp/surface',
    abbreviation: 'MOP',
  },
  distopalatal: {
    name: 'Distopalatal',
    description: 'Distopalatal cusp/surface',
    abbreviation: 'DOP',
  },
  mesiolingual: {
    name: 'Mesiolingual',
    description: 'Mesiolingual cusp/surface',
    abbreviation: 'MOL',
  },
  distolingual: {
    name: 'Distolingual',
    description: 'Distolingual cusp/surface',
    abbreviation: 'DOL',
  },

  // Cervical
  cervical: {
    name: 'Cervical',
    description: 'Cervical area',
    abbreviation: 'C',
  },
}

const toTitleCase = (value: string): string => {
  return value
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Helper functions
export const getConditionName = (code: string): string => {
  const normalizedCode = code?.trim()
  if (!normalizedCode) return ''

  return (
    CONDITIONS[normalizedCode]?.name ||
    CONDITIONS[normalizedCode.toLowerCase()]?.name ||
    toTitleCase(normalizedCode)
  )
}

export const getConditionMetadata = (
  code: string,
): ConditionMetadata | null => {
  return CONDITIONS[code] || null
}

export const getTypeName = (code: string): string => {
  const normalizedCode = code?.trim()
  if (!normalizedCode) return ''

  return (
    TYPES[normalizedCode]?.name ||
    TYPES[normalizedCode.toUpperCase()]?.name ||
    TYPES[normalizedCode.toLowerCase()]?.name ||
    toTitleCase(normalizedCode)
  )
}

export const getMaterialName = (code: string): string => {
  const normalizedCode = code?.trim()
  if (!normalizedCode) return ''

  return (
    MATERIALS[normalizedCode]?.name ||
    MATERIALS[normalizedCode.toLowerCase()]?.name ||
    toTitleCase(normalizedCode)
  )
}

export const getSurfaceName = (code: string): string => {
  const normalizedCode = code?.trim()
  if (!normalizedCode) return ''

  return (
    SURFACES[normalizedCode]?.name ||
    SURFACES[normalizedCode.toLowerCase()]?.name ||
    toTitleCase(normalizedCode)
  )
}

export const getValidTypesForCondition = (condition: string): string[] => {
  return CONDITIONS[condition]?.validTypes || []
}

export const getValidMaterialsForCondition = (condition: string): string[] => {
  return CONDITIONS[condition]?.validMaterials || []
}

export const canConditionHaveType = (condition: string): boolean => {
  return CONDITIONS[condition]?.allowsType || false
}

export const canConditionHaveMaterial = (condition: string): boolean => {
  return CONDITIONS[condition]?.allowsMaterial || false
}

export const canConditionBeRecurrent = (condition: string): boolean => {
  return CONDITIONS[condition]?.canBeRecurrent || false
}

// Format a complete dental condition for display
export const formatDentalCondition = (
  condition: string,
  type: string | null,
  material: string | null,
  surface: string | null,
  recurrent: boolean,
): string => {
  let display = getConditionName(condition)

  if (type) {
    display += ` (${getTypeName(type)})`
  }

  if (material) {
    display += ` (${getMaterialName(material)})`
  }

  if (surface) {
    display += ` - ${getSurfaceName(surface)}`
  }

  if (recurrent) {
    display += ' [Recurrent]'
  }

  return display
}

// ---------------------------------------------------------------------------
// Side panel: grouped labels, surface maps, restrictions, label → code
// ---------------------------------------------------------------------------

export const EXCLUSIVE_CONDITIONS: ToothCondition[] = [
  'missing',
  'implant',
  'root_canal',
  'pulpotomy',
  'pulpectomy',
  'bridge',
  'retainer',
  'fracture',
  'furcation',
]

/** Primary "Condition" accordion (display order) */
export const PANEL_CONDITION_LABELS = [
  'Caries',
  'Recurring caries',
  'Filling',
  'Abrasion/abfraction',
  'Erosion',
  'Attrition',
  'Recession',
  'Furcation',
  'Crack',
  'Fracture',
  'Crown',
  'Inlay',
  'Onlay',
  'Veneer',
  'Sealants',
  'Missing',
  'Implant',
  'Bridges',
  'Abutments',
]

export const PANEL_ENDODONTIC_LABELS = [
  'Root canal',
  'Apicoectomy',
  'Periapical',
  'Periradicular',
  'Pulpotomy',
  'Pulpectomy',
  'Sinus tract',
]

export const PANEL_ORTHODONTIC_LABELS = [
  'Impacted',
  'Drifted',
  'Supernumerary',
  'Retained primary',
]

export const SURFACE_LABEL_MAP: Record<string, string> = {
  Mesial: 'm',
  Distal: 'd',
  Buccal: 'b',
  Facial: 'b',
  'Buccal Cusp': 'bc',
  'Facial Cusp': 'bc',
  'Buccal Surface': 'bs',
  'Facial Surface': 'bs',
  Occlusal: 'o',
  Incisal: 'i',
  Palatal: 'p',
  'Palatal Cusp': 'pc',
  'Palatal Surface': 'ps',
  'Cervical Buccal': 'cb',
  'Cervical Facial': 'cb',
  'Cervical Palatal': 'cp',
  'Mesio Buccal Cusp': 'mb',
  'Mesio Facial Cusp': 'mb',
  'Disto Buccal Cusp': 'db',
  'Disto Facial Cusp': 'db',
  'Mesio Palatal Cusp': 'mp',
  'Disto Palatal Cusp': 'dp',
  Lingual: 'l',
  'Lingual Surface': 'ls',
  'Lingual Cusp': 'lc',
  'Cervical Lingual': 'cl',
  'Disto Lingual Cusp': 'dlc',
  'Mesio Lingual Cusp': 'mlc',
  MIFL: 'mifl',
  DIFL: 'difl',
  MIFP: 'mifp',
  DIFP: 'difp',
}

export const CUSP_RELATED_SURFACES: Record<string, string[]> = {
  mb: ['m', 'o', 'b'],
  db: ['d', 'o', 'b'],
  mp: ['m', 'o', 'p'],
  dp: ['d', 'o', 'p'],
  mlc: ['m', 'o', 'l'],
  dlc: ['d', 'o', 'l'],
  mifp: ['m', 'i', 'b', 'p'],
  difp: ['d', 'i', 'b', 'p'],
  mifl: ['m', 'i', 'b', 'l'],
  difl: ['d', 'i', 'b', 'l'],
}

/** Atomic surface sets that imply a compound cusp/surface code (MOB/MIFP/…). */
export const ATOMIC_SURFACES_TO_CUSP: ReadonlyArray<{
  surfaces: readonly string[]
  cusp: string
}> = [
  { surfaces: ['m', 'o', 'b'], cusp: 'mb' },
  { surfaces: ['d', 'o', 'b'], cusp: 'db' },
  { surfaces: ['m', 'o', 'p'], cusp: 'mp' },
  { surfaces: ['d', 'o', 'p'], cusp: 'dp' },
  { surfaces: ['m', 'o', 'l'], cusp: 'mlc' },
  { surfaces: ['d', 'o', 'l'], cusp: 'dlc' },
  { surfaces: ['m', 'i', 'b', 'p'], cusp: 'mifp' },
  { surfaces: ['d', 'i', 'b', 'p'], cusp: 'difp' },
  { surfaces: ['m', 'i', 'b', 'l'], cusp: 'mifl' },
  { surfaces: ['d', 'i', 'b', 'l'], cusp: 'difl' },
]

export const isCompoundCuspSurfaceCode = (surfaceCode: string): boolean => {
  return Object.prototype.hasOwnProperty.call(
    CUSP_RELATED_SURFACES,
    surfaceCode,
  )
}

export const getCuspsImpliedByAtomics = (
  selectedSurfaces: Set<string>,
): string[] => {
  return ATOMIC_SURFACES_TO_CUSP.filter(({ surfaces }) =>
    surfaces.every((surface) => selectedSurfaces.has(surface)),
  ).map(({ cusp }) => cusp)
}

export const getSurfaceToggleDiff = (
  surfaceCode: string,
  selectedSurfaces: Set<string>,
  autoImplyCusps: boolean,
): { toAdd: string[]; toRemove: string[] } => {
  const relatedSurfaces = CUSP_RELATED_SURFACES[surfaceCode]

  if (relatedSurfaces) {
    const atomicsFullyPresent = relatedSurfaces.every((atomic) =>
      selectedSurfaces.has(atomic),
    )
    const cuspIsSelected =
      selectedSurfaces.has(surfaceCode) ||
      (autoImplyCusps && atomicsFullyPresent)

    if (!cuspIsSelected) {
      const nextSelected = new Set(selectedSurfaces)
      nextSelected.add(surfaceCode)
      relatedSurfaces.forEach((atomic) => {
        nextSelected.add(atomic)
      })
      const toAdd = [surfaceCode, ...relatedSurfaces].filter(
        (surface) => !selectedSurfaces.has(surface),
      )
      if (autoImplyCusps) {
        getCuspsImpliedByAtomics(nextSelected).forEach((cusp) => {
          if (!selectedSurfaces.has(cusp) && !toAdd.includes(cusp)) {
            toAdd.push(cusp)
          }
        })
      }
      return { toAdd, toRemove: [] }
    }

    const otherSelectedCusps = Array.from(selectedSurfaces).filter(
      (selectedCode) =>
        selectedCode !== surfaceCode && isCompoundCuspSurfaceCode(selectedCode),
    )
    const protectedAtomics = new Set<string>()
    otherSelectedCusps.forEach((cusp) => {
      CUSP_RELATED_SURFACES[cusp]?.forEach((atomic) => {
        protectedAtomics.add(atomic)
      })
    })

    const toRemove = [
      surfaceCode,
      ...relatedSurfaces.filter((atomic) => !protectedAtomics.has(atomic)),
    ]
    return { toAdd: [], toRemove }
  }

  const isSelecting = !selectedSurfaces.has(surfaceCode)
  if (isSelecting) {
    const nextSelected = new Set(selectedSurfaces)
    nextSelected.add(surfaceCode)
    const toAdd = [surfaceCode]
    if (autoImplyCusps) {
      getCuspsImpliedByAtomics(nextSelected).forEach((cusp) => {
        if (!selectedSurfaces.has(cusp)) {
          toAdd.push(cusp)
        }
      })
    }
    return { toAdd, toRemove: [] }
  }

  const toRemove = [surfaceCode]
  Object.entries(CUSP_RELATED_SURFACES).forEach(([cusp, related]) => {
    if (selectedSurfaces.has(cusp) && related.includes(surfaceCode)) {
      toRemove.push(cusp)
    }
  })
  return { toAdd: [], toRemove }
}

export const REGION_DEPENDENT_CONDITIONS: ToothCondition[] = [
  'caries',
  'recurring_caries',
  'filling',
  'erosion',
  'inlay',
  'onlay',
  'drifted',
]

export const FURCATION_ALLOWED_TOOTH_IDS = [
  1, 2, 3, 14, 15, 16, 17, 18, 19, 30, 31, 32,
]

export const VENEER_RESTRICTED_TOOTH_IDS: number[] = []

export const INLAY_ONLAY_RESTRICTED_TOOTH_IDS = [
  6, 7, 8, 9, 10, 11, 22, 23, 24, 25, 26, 27,
]

/** No teeth restricted; sealants may be charted on anterior and posterior teeth. */
export const SEALANTS_RESTRICTED_TOOTH_IDS: number[] = []

export const RETAINER_ALLOWED_TOOTH_IDS = [
  4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29,
]

/** Voice/UI shared guard: returns a reject message when the condition cannot apply on this tooth. */
export const dentalConditionToothRestrictionMessage = (
  condition: string,
  tooth: number,
): string | undefined => {
  if (condition === 'furcation' && !FURCATION_ALLOWED_TOOTH_IDS.includes(tooth))
    return `Furcation is not applicable to tooth ${tooth}. Use molars 1–3, 14–16, 17–19, or 30–32.`
  if (
    (condition === 'inlay' || condition === 'onlay') &&
    INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(tooth)
  )
    return `${condition === 'inlay' ? 'Inlay' : 'Onlay'} is not applicable to tooth ${tooth}. Use posterior teeth 1–5, 12–16, 17–21, or 28–32.`
  if (condition === 'retainer' && !RETAINER_ALLOWED_TOOTH_IDS.includes(tooth))
    return `Retainer is not applicable to tooth ${tooth}. Use teeth 4–13 or 20–29.`
  if (condition === 'sealants' && SEALANTS_RESTRICTED_TOOTH_IDS.includes(tooth))
    return `Sealants are not applicable to tooth ${tooth}.`
  if (condition === 'veneer' && VENEER_RESTRICTED_TOOTH_IDS.includes(tooth))
    return `Veneer is not applicable to tooth ${tooth}.`
  return undefined
}

export const MOLAR_TOOTH_IDS = [1, 2, 3, 14, 15, 16]
export const PREMOLAR_TOOTH_IDS = [4, 5, 12, 13]
export const INCISOR_CANINE_TOOTH_IDS = [6, 7, 8, 9, 10, 11]
export const LOWER_MOLAR_TOOTH_IDS = [17, 18, 19, 30, 31, 32]
export const LOWER_PREMOLAR_TOOTH_IDS = [20, 21, 28, 29]
export const LOWER_INCISOR_CANINE_TOOTH_IDS = [22, 23, 24, 25, 26, 27]

export type TreatmentToothStatus = {
  id: number
  status: 'None' | 'Planned' | 'Treated' | 'Missing'
}

export type ChartingTabButton = {
  label: string
  value: 'home' | 'perio' | 'treatment-plan' | 'history'
}

export const tabButtons: ChartingTabButton[] = [
  { label: 'Dental', value: 'home' },
  { label: 'Perio', value: 'perio' },
  { label: 'Plan', value: 'treatment-plan' },
  { label: 'History', value: 'history' },
]

export const EmptyPerioChart: IPerioChart = {
  chart_id: '',
  patient_id: '',
  provider_id: '',
  clinic_id: '',
  title: '',
  description: '',
  perio_data: createEmptyPerioChartData(),
  notes: [],
}

export const EmptyDentalChart: IDentalChart = {
  chart_id: '',
  patient_id: '',
  provider_id: '',
  clinic_id: '',
  title: '',
  description: '',
  dental_data: [],
  notes: [],
}

export const initialUpperTeeth: TreatmentToothStatus[] = Array.from(
  { length: 16 },
  (_, index) => ({
    id: index + 1,
    status: 'None',
  }),
)

export const initialLowerTeeth: TreatmentToothStatus[] = Array.from(
  { length: 16 },
  (_, index) => ({
    id: index + 17,
    status: 'None',
  }),
)

/** Canonical furcation class codes (storage + UI); maps to validTypes on `furcation`. */
export const FURCATION_TYPE_CODES = ['C1', 'C2', 'C3'] as const

/**
 * Conditions that show type/material/surface sub-items in the side panel (includes ortho).
 */
export const CONDITION_CODES_WITH_SUB_ITEMS: readonly string[] = [
  'caries',
  'recurring_caries',
  'filling',
  'erosion',
  'inlay',
  'onlay',
  'abrasion',
  'crown',
  'furcation',
  'recession',
  'periradicular',
  'crack',
  'fracture',
  'attrition',
  'bridge',
  'abutment',
  'veneer',
  'drifted',
  'impacted',
]

/** Main "Condition" accordion auto-expand (excludes ortho-only `drifted` / `impacted`). */
export const PANEL_AUTO_EXPAND_CONDITION_CODES: readonly string[] = [
  'caries',
  'recurring_caries',
  'filling',
  'erosion',
  'inlay',
  'onlay',
  'abrasion',
  'crown',
  'furcation',
  'recession',
  'periradicular',
  'crack',
  'fracture',
  'attrition',
  'bridge',
  'abutment',
  'veneer',
]

export const ORTHODONTIC_CODES_WITH_SUB_ITEMS: readonly string[] = [
  'drifted',
  'impacted',
]

/** Conditions that use the per-tooth surface grid in the side panel. */
export const CONDITION_CODES_WITH_SURFACE_GRID: readonly string[] = [
  'caries',
  'recurring_caries',
  'filling',
  'erosion',
  'abrasion',
  'inlay',
  'onlay',
]

/**
 * Map panel label (as shown in the UI) → canonical condition code used in `IDentalData`.
 */
export const LABEL_TO_CONDITION: Record<string, string> = {
  Caries: 'caries',
  'Recurring caries': 'recurring_caries',
  Inlay: 'inlay',
  Onlay: 'onlay',
  Abrasion: 'abrasion',
  'Abrasion/abfraction': 'abrasion',
  Erosion: 'erosion',
  Implant: 'implant',
  Veneer: 'veneer',
  Filling: 'filling',
  Crown: 'crown',
  Furcation: 'furcation',
  Recession: 'recession',
  Attrition: 'attrition',
  Bridges: 'bridge',
  Abutments: 'abutment',
  Sealants: 'sealants',
  Missing: 'missing',
  Crack: 'crack',
  Fracture: 'fracture',
  'Root canal': 'root_canal',
  Apicoectomy: 'apicoectomy',
  Periapical: 'periapical',
  Periradicular: 'periradicular',
  Pulpotomy: 'pulpotomy',
  Pulpectomy: 'pulpectomy',
  'Sinus tract': 'sinus_tract',
  Impacted: 'impacted',
  Drifted: 'drifted',
  Supernumerary: 'supernumerary',
  'Retained primary': 'retainer',
}
